import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { Typography, TextField, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import ClearIcon from "@mui/icons-material/Clear";
import { getEventLog, publicExportedFileLink } from "../Service";
import moment from "moment";
import LoadingButton from "@mui/lab/LoadingButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { addLog } from "../../utils/addLog";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { signOut } from "../../utils/signOut";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450,
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
};

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: 2.5,
      }}
    >
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function Logging() {
  const [t, i18n] = useTranslation("global");

  const [userRole, setUserRole] = React.useState(
    JSON.parse(sessionStorage.getItem("authUserRole"))
  );
  const [data, setData] = useState({ total: 0 });
  const [firstLoadData, setFirstLoadData] = useState(true);

  const [dataSource, setDataSource] = useState([]);

  const styles = {
    BtnIcon: {
      width: 32,
      height: 32,
    },
  };

  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const handleChangeDateStart = (newValue) => {
    setDateStart(newValue);
    if (dateEnd && newValue && newValue.isAfter(dateEnd)) {
      setSumErrorMsg(t("logging.warning.date_start"));
      handleClickSnack();
    }
  };
  const handleChangeDateEnd = (newValue) => {
    setDateEnd(newValue);
    if (dateStart && newValue && newValue.isBefore(dateStart)) {
      setSumErrorMsg(t("logging.warning.date_end"));
      handleClickSnack();
    }
  };
  const handleClearStart = () => {
    setDateStart(null);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [name, setName] = useState("");

  const [errorMsg, setErrorMsg] = useState("");
  const [sumErrorMsg, setSumErrorMsg] = useState("");

  const [loading, setLoading] = useState(false);

  const handleClearSearch = () => {
    setName("");
  };

  const columns = [
    {
      id: "User",
      label: t("logging.table.header.name"),
      align: "left",
    },
    {
      id: "Date",
      label: t("logging.table.header.date"),
      align: "left",
    },
    {
      id: "Event",
      label: t("logging.table.header.event"),
      align: "left",
    },
    {
      id: "Description",
      label: t("logging.table.header.description"),
      align: "left",
    },
  ];

  // Snack Bar
  const vertical = "top";
  const horizontal = "right";
  const [openSnack, setOpenSnack] = useState(false);

  const handleClickSnack = () => {
    setOpenSnack(true);
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnack(false);
  };

  React.useEffect(() => {
    if (firstLoadData) {
      setFirstLoadData(false);
      getData(0, rowsPerPage);
    }
  }, []);

  const getData = async (nowPage, nowRowsPerPage) => {
    setLoading(true);

    var nowPages = nowPage + 1;

    let qString = `?page=${nowPages}&perpage=${nowRowsPerPage}`;
    qString += "&role=" + userRole.role;
    if (name) qString += "&name=" + name;
    if (dateStart)
      qString += "&date_start=" + dayjs(dateStart).format("YYYY-MM-DD");
    if (dateEnd) qString += "&date_end=" + dayjs(dateEnd).format("YYYY-MM-DD");

    await getEventLog(qString).then((res) => {
      if (res && res?.status === 200) {
        // For paginate
        setDataSource(res.data.data);
        setData(res.data);
      } else if (res?.status === 401) {
        signOut();
      } else if (res === undefined) {
        Swal.fire({
          icon: "error",
          title: "Database connection failed",
          text: "Please contact administrator",
        });
      } else {
        console.log(`error getEventLog:`, res?.data);
      }
      setLoading(false);
    });
  };

  function getFileUrl(file) {
    const dataForm = {
      filename: file,
    };

    publicExportedFileLink(dataForm).then((res) => {
      if (res && res.status === 200 && res.data.success === true) {
        const newWindow = window.open(
          res.data.link,
          "_blank",
          "noopener,noreferrer"
        );
        if (newWindow) newWindow.opener = null;
      } else if (res.status === 401) {
        signOut();
      }
    });
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getData(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    getData(0, event.target.value);
  };

  const handleClickSearch = async () => {
    // Start date or end date must not be blank.
    if (
      (!dateStart && dateEnd) ||
      (dateStart && !dateEnd) ||
      (dateStart && dateStart.isAfter(dateEnd))
    ) {
      Swal.fire({
        icon: "warning",
        showConfirmButton: false,
        text: t("logging.warning.date"),
      });
      return;
    }

    setLoading(true);
    setPage(0);
    await getData(0, rowsPerPage);
    addLog("Log", "Search", null);
  };

  return (
    <Box
      style={{
        margin: "65px 0px 0px 0px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Snackbar
        open={openSnack}
        autoHideDuration={3000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnack}
          severity="warning"
          sx={{ width: "100%", fontSize: "16px", padding: 2 }}
        >
          {sumErrorMsg}
        </Alert>
      </Snackbar>

      {/*  ////////////////////////// Main Topic Pages ////////////////////////// */}

      <Box style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography
          style={{
            fontWeight: "400",
            color: "#5C5C5C",
            fontSize: "1.8rem",
          }}
        >
          {t("logging.title")}
        </Typography>
      </Box>

      {/*  ////////////////////////// Search Box ////////////////////////// */}
      <Box
        sx={{
          display: "flex",
          paddingBottom: "20px",
          margin: "35px 0px 0px 0px",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Box sx={{ width: { lg: "400px" }, display: "flex", mr: 2 }}>
            <TextField
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderColor: "white",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "white",
                },
                "& .MuiInputLabel-root": {
                  transform: "translate(10px,-25px)", // Adjust to change label position
                },
              }}
              label={t("logging.txtbox.search")}
              size="middle"
              variant="outlined"
              InputProps={{
                style: {
                  borderRadius: "10px",
                  backgroundColor: "#F0F0F0",
                  paddingLeft: "15px",
                  fontSize: "medium",
                },
                endAdornment: (
                  <IconButton
                    sx={{
                      visibility: name ? "visible" : "hidden",
                    }}
                    onClick={handleClearSearch}
                  >
                    <ClearIcon />
                  </IconButton>
                ),
              }}
              fullWidth
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
            <Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t("logging.txtbox.date")}
                  disableFuture
                  value={dateStart}
                  onChange={handleChangeDateStart}
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: () => setDateStart(null),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderColor: "white",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "white",
                    },
                    "& .MuiInputLabel-root": {
                      transform: "translate(10px,-25px)", // Adjust to change label position
                    },
                    "& .MuiInputBase-root": {
                      backgroundColor: "#F0F0F0", // Set your background color here
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ margin: "0px 10px 0px 10px" }}>
              <Typography>-</Typography>
            </Box>
            <Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label=" "
                  disableFuture
                  value={dateEnd}
                  onChange={handleChangeDateEnd}
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: () => setDateEnd(null),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderColor: "white",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "white",
                    },
                    "& .MuiInputLabel-root": {
                      transform: "translate(10px,-25px)", // Adjust to change label position
                    },
                    "& .MuiInputBase-root": {
                      backgroundColor: "#F0F0F0", // Set your background color here
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box sx={{ width: 200, mr: 2 }}>
            <LoadingButton
              onClick={handleClickSearch}
              loadingIndicator="Loading…"
              variant="contained"
              size="small"
              style={{
                backgroundColor: "#5C5C5C",
                color: "#FFFFFF",
                borderRadius: "10px",
                height: "45px",
                textTransform: "none",
                width: "100px",
              }}
            >
              {t("common.button.search")}
            </LoadingButton>
          </Box>
        </Box>
        <Box>
          {dataSource.length > 0 &&
          dataSource.filter((item) => item.isChecked === true).length > 0 ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Box sx={{ mr: 2 }}>
                  <Typography>
                    Selected &nbsp;
                    {
                      dataSource.filter((item) => item.isChecked === true)
                        .length
                    }
                    &nbsp; items
                  </Typography>
                </Box>
              </Box>
            </>
          ) : (
            ""
          )}
        </Box>
      </Box>

      {/*  ////////////////////////// Data Table ////////////////////////// */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    backgroundColor: "#c4e6ff",
                    padding: "10px",
                    fontWeight: "bold",
                    color: "#1D589D",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress color="inherit" />
                  </Box>
                </TableCell>
              </TableRow>
            ) : dataSource.length > 0 ? (
              dataSource.map((row, index) => (
                <TableRow key={index}>
                  <TableCell style={{ minWidth: 300 }}>
                    <Typography>{row.name}</Typography>
                    <Typography color="gray">{row.email}</Typography>
                  </TableCell>
                  <TableCell style={{ minWidth: 150 }}>
                    <Typography>
                      {row.created_at
                        ? moment(row.created_at).format("MMM DD YYYY")
                        : ""}
                    </Typography>
                    <Typography color="gray">
                      at &nbsp;
                      {row.created_at
                        ? moment(row.created_at).format("hh:mmA")
                        : ""}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ minWidth: 150 }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                      <Typography color="gray">{row.event_action}</Typography>
                      &nbsp;
                      <Typography>{row.event_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={{ minWidth: 200 }} align="left">
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {row.file_boq ? (
                        <Button
                          variant="text"
                          style={{
                            textTransform: "none",
                            fontSize: "16px",
                            textAlign: "left",
                            display: "flex",
                            justifyContent: "flex-start",
                          }}
                          size="small"
                          onClick={() => getFileUrl(row.file_boq)}
                        >
                          {row.file_boq}
                        </Button>
                      ) : (
                        ""
                      )}

                      {row.file_tor ? (
                        <Button
                          variant="text"
                          style={{
                            textTransform: "none",
                            fontSize: "16px",
                            textAlign: "left",
                            display: "flex",
                            justifyContent: "flex-start",
                          }}
                          size="small"
                          onClick={() => getFileUrl(row.file_tor)}
                        >
                          {row.file_tor}
                        </Button>
                      ) : (
                        ""
                      )}

                      {row.file_qt ? (
                        <Button
                          variant="text"
                          style={{
                            textTransform: "none",
                            fontSize: "16px",
                            textAlign: "left",
                            display: "flex",
                            justifyContent: "flex-start",
                          }}
                          size="small"
                          onClick={() => getFileUrl(row.file_qt)}
                        >
                          {row.file_qt}
                        </Button>
                      ) : (
                        ""
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box
                    style={{ display: "flex", justifyContent: "center" }}
                  ></Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                colSpan={4}
                count={data.total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
}
